import {ChangeDetectorRef, Component, ElementRef, inject, viewChild, output} from '@angular/core';
import { ConfirmationService, MessageService, SelectItem, PrimeTemplate } from 'primeng/api';
import {VerifiedDomainsService} from '@app/shared/_services/verified-domains.service';
import {TranslateService} from '@app/shared/_services/translate.service';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {VerifiedDomain} from '@app/shared/_components/verified-domains/verified-domains.interface';
import {catchError} from 'rxjs/operators';
import {debounceTime, distinctUntilChanged, throwError} from 'rxjs';
import {UserService} from '@app/shared/_services/user.service';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import {Subaccount} from '@app/features/subaccounts/subaccounts.interface';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { RadioButtonModule } from 'primeng/radiobutton';
import { Button, ButtonDirective } from 'primeng/button';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { CheckboxModule } from 'primeng/checkbox';
import { TranslatePipe } from '../../_pipes/translate.pipe';

@Component({
    selector: 'app-verified-domains',
    templateUrl: './verified-domains.component.html',
    providers: [ConfirmationService],
    imports: [ConfirmDialogModule, DialogModule, FormsModule, ReactiveFormsModule, RadioButtonModule, Button, TableModule, PrimeTemplate, ToggleButtonModule, InputTextModule, IconFieldModule, InputIconModule, SelectModule, TooltipModule, ButtonDirective, MessageModule, CheckboxModule, TranslatePipe]
})
export class VerifiedDomainsComponent {
  verifiedDomains: VerifiedDomain.Item[] = [];
  customDomainsLimit = 10;
  verifiedDomainsCount = 0;
  verifiedDomainsLoading = false;
  isSubaccount: boolean = false;
  showFilters = new FormControl(false);
  hasFiltersActive: boolean = false;

  readonly verifiedDomainsTable = viewChild.required<Table>('verifiedDomainsTable');
  readonly notificationField = viewChild<ElementRef<HTMLElement>>('notificationField');

  private domainService = inject(VerifiedDomainsService);
  private confirmationService = inject(ConfirmationService);
  private _translateService = inject(TranslateService);
  private _messageService = inject(MessageService);
  private _userService = inject(UserService);
  private _cdr = inject(ChangeDetectorRef);

  readonly onOpenCreateDomain = output();
  readonly onOpenVerifyDomain = output<{
    domainId: number;
}>();
  unverifiedSenderActionModalVisible: boolean;
  unverifiedSenderDomainForm = new FormGroup({
    unverified_domain_action: new FormControl<VerifiedDomain.UnverifiedSenderAction>(null)
  })
  unverifiedSenderCurrentAction: VerifiedDomain.UnverifiedSenderAction;
  unverifiedSenderDomainActions: VerifiedDomain.UnverifiedSenderAction[] = [];

  senderDomainNotificationForm = new FormGroup({
    enabled: new FormControl<boolean>(false),
    notification_email: new FormControl<string>('', [Validators.email])
  });

  saveNotificationEmailLoading = false;

  verifiedDomainForm = new FormGroup({
    search: new FormControl<string>(""),
    filterByVerification: new FormControl<VerifiedDomain.ItemVerified | null>(null)
  });


  verificationTypes : SelectItem<VerifiedDomain.ItemVerified>[] = [
    {
      label: this._translateService.translate('settings_sender_domains_status_verified'),
      value: 'verified'
    },
    {
      label: this._translateService.translate('settings_sender_domains_status_not_verified'),
      value: 'unverified'
    }
  ];

  constructor() {
    this.getDomainActions();
    this.updateNotificationForm();
    this.isSubaccount = this._userService.getInfoValue('is_subaccount') as boolean;
    this.verifiedDomainForm.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(

        )
      )
      .subscribe(
        ({search, filterByVerification}) =>{
          this.getDomains({
            ...search && {search},
            ...filterByVerification && {filterByVerification},
            page: 1,
            limit: this.verifiedDomainsTable().rows,
            ...typeof this.verifiedDomainsTable().sortField !== 'undefined' && {
              orderby: <VerifiedDomain.OrderBy>this.verifiedDomainsTable().sortField,
              ordertype: this.verifiedDomainsTable().sortOrder === 1 ? 'asc' : 'desc'
            }



          });

          this.hasFiltersActive = true;

        }
      )
  }

  getDomainActions() {
    this.domainService.getDomainActions()
      .subscribe({
        next: ({actions}) => {
          this.unverifiedSenderDomainActions = actions.actions;
          this.unverifiedSenderCurrentAction = actions.current_action;
          this.unverifiedSenderDomainForm.get('unverified_domain_action').patchValue(actions.current_action);
          this._cdr.detectChanges();
        }
      });
  }

  getDomains(params?: any){
    this.verifiedDomainsLoading = true;
    this.domainService.getDomains(
      params
    )
      .subscribe({
        next: data => {
          this.verifiedDomains = data.results;
          this.verifiedDomainsCount = data.count;
          this.verifiedDomainsLoading = false;
          this._cdr.detectChanges();
        },
        error: error => {
          console.error('Error fetching domains', error);
          this.verifiedDomainsLoading = false;
          this._cdr.detectChanges();
        }
      });
  }

  loadVerifiedDomains(event: TableLazyLoadEvent) {

    const page = event.first / event.rows + 1;
    const limit = event.rows;
    const search = this.verifiedDomainForm.get('search').value;
    const filterByVerification = this.verifiedDomainForm.get('filterByVerification').value;


    this.getDomains(
      {
        page,
        limit,
        ...search && {search},
        ...filterByVerification && {filterByVerification},
        ...typeof event.sortField !== 'undefined' && {
          orderby: <Subaccount.OrderBy>event.sortField,
          ordertype: event.sortOrder === 1 ? 'asc' : 'desc'
        }
      }
    );


  }


  deleteDomain(domainId: number) {
    this.domainService.deleteDomain(domainId).subscribe({
      next: _ => {
        this.getDomains();
        this._cdr.detectChanges();
      },
      error: error => {
        console.error('Error deleting domain', error);
        this._cdr.detectChanges();
      }
    });
  }

  confirmDelete(domainId: number, domainName: string) {
    console.log(domainName);
    this.confirmationService.confirm({
      header: this._translateService.translate('settings_sender_action_delete_confirm_title'),
      message: this._translateService.translate('settings_sender_action_delete_confirm_message', {'domainName': domainName}),
      acceptLabel: this._translateService.translate('button_yes_delete'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteDomain(domainId);
      }
    });
  }

  openCreateDomain() {
    this.onOpenCreateDomain.emit();
  }

  openVerifyDomain(domainId: number) {
    this.onOpenVerifyDomain.emit({domainId})
  }

  unverifiedSenderModalClose() {
    this.unverifiedSenderActionModalVisible = false;
  }

  private enableUnverifiedSender(action: VerifiedDomain.UnverifiedSenderAction) {
    this.domainService.updateDomainAction(action).subscribe({
      next: () => {
        this.unverifiedSenderActionModalVisible = false;
        this.unverifiedSenderCurrentAction = action;
        this._cdr.detectChanges();
      }
    })
  }

  unverifiedSenderModalSave() {
    this.unverifiedSenderActionModalVisible = false;
    if (
      this.unverifiedSenderDomainForm.get('unverified_domain_action').value !== this.unverifiedSenderCurrentAction
    ) {
      this.enableUnverifiedSender(
        this.unverifiedSenderDomainForm.get('unverified_domain_action').value
      );
    }
  }


  updateNotificationForm() {
    this.domainService.getNotificationEmail()
      .subscribe({
          next: value => {
            this.senderDomainNotificationForm.patchValue(value)
            this._cdr.detectChanges();
          }
        }
      );
  }

  saveNotificationEmail() {
    this.saveNotificationEmailLoading = true;
    this.domainService.updateNotificationEmail(
      this.senderDomainNotificationForm.get('notification_email').value,
      this.senderDomainNotificationForm.get('enabled').value
    ).pipe(
      catchError(err => {
        this._messageService.add({
          severity: 'error',
          summary: this._translateService.translate(err.error.message)
        });
        return throwError(err);
      })
    ).subscribe({
        next: ({message}) => {
          this.updateNotificationForm();
          this.saveNotificationEmailLoading = false;
          this._messageService.add({
            severity: 'success',
            summary: this._translateService.translate(message)
          });
          this._cdr.detectChanges();
        },
        error: _ => {
          this.saveNotificationEmailLoading = false;
          this._cdr.detectChanges();
        }
      }
    );
  }

  enableSendNotification(enabled: boolean) {
    if (enabled) {
      // the email field renders via @if once the checkbox is on; wait for that
      // render, then reveal it (it's below the dialog's scroll fold) with an
      // animated scroll so the user sees they must specify an address. Scroll the
      // dialog's own content to its very bottom rather than scrollIntoView, which
      // leaves the container's padding-bottom (~20px) unscrolled.
      setTimeout(() => {
        const field = this.notificationField()?.nativeElement;
        const scrollContainer = field?.closest('.p-dialog-content') as HTMLElement | null;
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
        } else {
          field?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      });
    }
    this.domainService.updateNotificationEmail(
      enabled ? this.senderDomainNotificationForm.get('notification_email').value: null,
      enabled
    ).subscribe({
      next: _ => {
        this.updateNotificationForm();
        this._messageService.add({
          severity: 'success',
          summary: this._translateService.translate(`notification_email_${enabled? 'enabled': 'disabled'}`)
        });
        this._cdr.detectChanges();
      }
    });
  }
}
